import type { ImmunizationResponse } from '@emr/shared';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';

import { listImmunizations } from '../../../api/chart.ts';
import type { DataTableColumn } from '../../../components/DataTable.tsx';
import { formatCalendarDate } from '../../../lib/dateFormat.ts';
import { ChartTable } from './ChartTable.tsx';

function columnsFor(
  t: TFunction<'patientChartTabs'>,
): readonly DataTableColumn<ImmunizationResponse>[] {
  return [
    {
      key: 'vaccine',
      header: t('immunizations.columns.vaccine'),
      cell: (immunization) => immunization.vaccine,
      wrap: 'anywhere',
      maxWidthCh: 26,
    },
    {
      key: 'administered',
      header: t('immunizations.columns.administered'),
      cell: (immunization) => formatCalendarDate(immunization.administeredDate),
      wrap: 'nowrap',
    },
    {
      key: 'lot',
      header: t('immunizations.columns.lot'),
      cell: (immunization) => immunization.lotNumber ?? '—',
      wrap: 'anywhere',
      maxWidthCh: 16,
      cellClassName: 'font-mono',
    },
  ];
}

export function ImmunizationsTab({ patientId }: { readonly patientId: string }) {
  const { t } = useTranslation('patientChartTabs');
  return (
    <ChartTable
      queryKey={['patient', patientId, 'immunizations']}
      queryFn={(signal) => listImmunizations(patientId, signal)}
      columns={columnsFor(t)}
      rowKey={(immunization) => immunization.id}
      minWidthRem={40}
      emptyMessage={t('immunizations.empty')}
      loadingLabel={t('immunizations.loading')}
      caption={t('immunizations.caption')}
    />
  );
}
